import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle, AlertTriangle, Bot, Building2, CheckCircle2, ChevronDown,
  ChevronRight, ChevronUp, Info, Mail, PlayCircle, Save, X,
} from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants.ts'
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  IconButton,
  LoadingState,
  PageHeader,
  QualityScoreBar,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
  type ColumnDef,
} from '@/components/ui'

// ---------- Typen ----------

interface QualityIssue {
  code: string
  message: string
  severity: 'error' | 'warning' | 'info'
}

interface DatenpflegeRow {
  id: string
  name: string
  bpType: 'customer' | 'supplier' | 'prospect'
  sapCardCode: string | null
  tenantCode: string | null
  industryName: string | null
  qualityScore: number
  qualityIssues: QualityIssue[]
}

interface QualitySummaryResponse {
  data: DatenpflegeRow[]
  total: number
  page: number
  pageSize: number
  pages: number
}

interface CategoryGp {
  id: string
  name: string
  bpType: string
  sapCardCode: string | null
  tenantCode: string | null
  industryName: string | null
}

interface QualityCategory {
  code: string
  severity: 'error' | 'warning' | 'info'
  count: number
  gps: CategoryGp[]
}

interface QualityByCategoryResponse {
  categories: QualityCategory[]
  totalGps: number
}

// ---------- Konstanten ----------

const TYPE_LABEL: Record<string, string> = {
  customer: 'Kunde',
  supplier: 'Lieferant',
  prospect: 'Interessent',
}

const TYPE_STATUS: Record<string, string> = {
  customer: 'active',
  supplier: 'info',
  prospect: 'pending',
}

const ISSUE_LABEL: Record<string, string> = {
  addr_street: 'Straße',
  addr_zipCode: 'PLZ',
  addr_city: 'Stadt',
  addr_country: 'Land',
  missing_vat: 'USt-ID',
  no_contact_email: 'E-Mail',
  no_contact_phone: 'Telefon',
  no_banking: 'Bankdaten',
  missing_website: 'Website',
  missing_sap_id: 'KdNr',
  missing_tenant: 'Mandant',
  missing_industry: 'Branche',
}

const SEVERITY_VARIANT: Record<string, 'danger' | 'warning' | 'neutral'> = {
  error: 'danger',
  warning: 'warning',
  info: 'neutral',
}

const ISSUE_EDITABLE_FIELD: Partial<Record<string, string>> = {
  missing_industry: 'industryName',
  missing_website: 'website',
  addr_street: 'street',
  addr_zipCode: 'zipCode',
  addr_city: 'city',
  addr_country: 'country',
}

// ---------- Filter-Typen ----------

type ScoreFilter = '' | 'lt100' | 'lt50'
type IssueCodeFilter = '' | 'no_contact_email' | 'no_banking' | 'missing_vat' | 'addr_street' | 'missing_sap_id' | 'missing_tenant' | 'missing_industry' | 'missing_website'
type TypeFilter = '' | 'customer' | 'supplier' | 'prospect'

function scoreFilterParam(f: ScoreFilter): string | undefined {
  if (f === 'lt100') return '99'
  if (f === 'lt50') return '49'
  return undefined
}

// ---------- Inline-Edit-Input ----------

function InlineEditCell({
  rowId,
  currentValue,
  edits,
  savedIds,
  failedIds,
  onChange,
}: {
  rowId: string
  currentValue: string | null
  edits: Map<string, string>
  savedIds: Set<string>
  failedIds: Map<string, string>
  onChange: (id: string, value: string) => void
}) {
  const editValue = edits.get(rowId)
  const isSaved = savedIds.has(rowId)
  const errorMsg = failedIds.get(rowId)
  const isDirty = editValue !== undefined && editValue !== (currentValue ?? '')

  let borderClass = 'border-gray-200 focus:border-brand-500'
  if (errorMsg) borderClass = 'border-red-400 bg-red-50'
  else if (isSaved) borderClass = 'border-green-400 bg-green-50'
  else if (isDirty) borderClass = 'border-amber-400 bg-amber-50'

  return (
    <div className="w-full">
      <input
        type="text"
        className={`w-full text-sm px-2 py-1 rounded border transition-colors outline-none ${borderClass}`}
        placeholder={currentValue ?? '—'}
        value={editValue ?? currentValue ?? ''}
        onChange={(e) => onChange(rowId, e.target.value)}
        onClick={(e) => e.stopPropagation()}
      />
      {errorMsg && (
        <p className="text-xs text-red-600 mt-0.5 truncate" title={errorMsg}>{errorMsg}</p>
      )}
    </div>
  )
}

// ---------- Severity-Icon ----------

function SeverityIcon({ severity }: { severity: 'error' | 'warning' | 'info' }) {
  if (severity === 'error') return <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
  if (severity === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
  return <Info className="w-4 h-4 text-blue-400 shrink-0" />
}

// ---------- Kategorien-Ansicht ----------

function KategorienAnsicht({ bpType }: { bpType: TypeFilter }) {
  const { toast } = useToast()
  const navigate = useNavigate()

  // Per-category selection: code → Set<id>
  const [selections, setSelections] = useState<Map<string, Set<string>>>(new Map())
  // Which categories are expanded
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const params = new URLSearchParams()
  if (bpType) params.set('bpType', bpType)

  const { data, isLoading, isError, error } = useQuery<QualityByCategoryResponse>({
    queryKey: ['gp-quality-by-category', bpType],
    queryFn: () =>
      apiClient.get(`/business-partners/quality-by-category?${params.toString()}`).then((r) => r.data),
  })

  const bulkEnrich = useMutation({
    mutationFn: (ids: string[]) =>
      apiClient.post('/business-partners/bulk-enrich', { bpIds: ids }).then((r) => r.data),
    onSuccess: (_result, ids) => {
      toast({ title: `KI-Anreicherung für ${ids.length} GPs gestartet`, tone: 'success' })
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler', description: err.message, tone: 'danger' })
    },
  })

  function toggleExpand(code: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  function toggleAll(code: string, gps: CategoryGp[]) {
    setSelections((prev) => {
      const next = new Map(prev)
      const sel = new Set(next.get(code) ?? [])
      if (sel.size === gps.length) {
        next.set(code, new Set())
      } else {
        next.set(code, new Set(gps.map((g) => g.id)))
      }
      return next
    })
  }

  function toggleGp(code: string, id: string) {
    setSelections((prev) => {
      const next = new Map(prev)
      const sel = new Set(next.get(code) ?? [])
      if (sel.has(id)) sel.delete(id)
      else sel.add(id)
      next.set(code, sel)
      return next
    })
  }

  function handleBulkEnrich(code: string) {
    const ids = [...(selections.get(code) ?? [])]
    if (!ids.length) {
      toast({ title: 'Keine GPs ausgewählt', tone: 'info' })
      return
    }
    bulkEnrich.mutate(ids)
  }

  function handleStartWizard(code: string) {
    const ids = [...(selections.get(code) ?? [])]
    const query = ids.length ? `?ids=${ids.join(',')}` : ''
    navigate(`/gp/datenpflege/wizard${query}`)
  }

  if (isLoading) return <LoadingState />
  if (isError) return <ErrorState title="Fehler beim Laden" description={(error as Error)?.message} />

  const categories = data?.categories ?? []

  if (categories.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 className="w-8 h-8 text-green-500" />}
        title="Alle Daten vollständig"
        description="Für die aktiven Filter wurden keine Datenlücken gefunden."
      />
    )
  }

  return (
    <div className="space-y-3">
      {categories.map((cat) => {
        const isOpen = expanded.has(cat.code)
        const sel = selections.get(cat.code) ?? new Set<string>()
        const allSel = sel.size === cat.gps.length
        const selCount = sel.size

        return (
          <div key={cat.code} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
            {/* Category header — click to expand */}
            <button
              type="button"
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              onClick={() => toggleExpand(cat.code)}
            >
              <SeverityIcon severity={cat.severity} />
              <span className="font-medium text-gray-900 flex-1">
                {ISSUE_LABEL[cat.code] ?? cat.code} fehlt
              </span>
              <Badge variant={SEVERITY_VARIANT[cat.severity] ?? 'neutral'}>
                {cat.count} {cat.count === 1 ? 'GP' : 'GPs'}
              </Badge>
              {isOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
              )}
            </button>

            {isOpen && (
              <>
                {/* Bulk-action bar (shows when GPs are selected) */}
                {selCount > 0 && (
                  <div className="flex items-center gap-3 px-4 py-2 bg-brand-50 border-t border-brand-200">
                    <span className="text-sm font-medium text-brand-700">
                      {selCount} {selCount === 1 ? 'GP' : 'GPs'} ausgewählt
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={bulkEnrich.isPending}
                      onClick={() => handleBulkEnrich(cat.code)}
                    >
                      <Bot className="w-4 h-4 mr-1" />
                      KI-Anreicherung
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleStartWizard(cat.code)}
                    >
                      <PlayCircle className="w-4 h-4 mr-1" />
                      Wizard starten
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const ids = [...(selections.get(cat.code) ?? [])]
                        const query = new URLSearchParams({ action: 'campaign' })
                        if (ids.length) query.set('ids', ids.join(','))
                        navigate(`/gp/datenpflege/wizard?${query.toString()}`)
                      }}
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Kampagne senden
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto"
                      onClick={() =>
                        setSelections((prev) => {
                          const next = new Map(prev)
                          next.set(cat.code, new Set())
                          return next
                        })
                      }
                    >
                      <X className="w-3 h-3 mr-1" />
                      Auswahl aufheben
                    </Button>
                  </div>
                )}

                {/* GP list */}
                <div className="divide-y divide-gray-100 border-t border-gray-100">
                  {/* Select-all row */}
                  <div className="flex items-center gap-3 px-4 py-2 bg-gray-50">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 accent-brand-700 cursor-pointer"
                      checked={allSel}
                      onChange={() => toggleAll(cat.code, cat.gps)}
                      aria-label="Alle auswählen"
                    />
                    <span className="text-xs text-gray-500 font-medium">Alle auswählen</span>
                  </div>

                  {cat.gps.map((gp) => (
                    <div
                      key={gp.id}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => toggleGp(cat.code, gp.id)}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 accent-brand-700 cursor-pointer"
                        checked={sel.has(gp.id)}
                        onChange={() => toggleGp(cat.code, gp.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`${gp.name} auswählen`}
                      />
                      <span
                        className="flex-1 text-sm text-gray-900 font-medium hover:text-brand-700 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/gp/${gp.id}`)
                        }}
                      >
                        {gp.name}
                      </span>
                      {gp.tenantCode && (
                        <StatusBadge status={gp.tenantCode} />
                      )}
                      <StatusBadge
                        status={TYPE_STATUS[gp.bpType] ?? 'default'}
                        label={TYPE_LABEL[gp.bpType] ?? gp.bpType}
                      />
                      {gp.sapCardCode && (
                        <code className="text-xs text-gray-500">{gp.sapCardCode}</code>
                      )}
                      <IconButton
                        icon={<ChevronRight className="w-4 h-4" />}
                        aria-label={`Wizard für ${gp.name}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/gp/datenpflege/wizard?id=${gp.id}`)
                        }}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------- Seite ----------

export default function GpDatenpflegeUebersichtPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter>('')
  const [issueFilter, setIssueFilter] = useState<IssueCodeFilter>('')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('')
  const [activeTab, setActiveTab] = useState<'alle' | 'kategorien'>('kategorien')

  // Inline-Edit-State
  const [inlineEdits, setInlineEdits] = useState<Map<string, string>>(new Map())
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set())
  const [failedIds, setFailedIds] = useState<Map<string, string>>(new Map())
  const inlineEditsRef = useRef(inlineEdits)
  inlineEditsRef.current = inlineEdits
  const savedIdsRef = useRef(savedIds)
  savedIdsRef.current = savedIds
  const failedIdsRef = useRef(failedIds)
  failedIdsRef.current = failedIds

  const editableField = ISSUE_EDITABLE_FIELD[issueFilter] ?? null

  const params = new URLSearchParams()
  params.set('page', '1')
  params.set('pageSize', '200')
  const scoreMax = scoreFilterParam(scoreFilter)
  if (scoreMax) params.set('scoreMax', scoreMax)
  if (issueFilter) params.set('issueCode', issueFilter)
  if (typeFilter) params.set('bpType', typeFilter)

  const { data, isLoading, isError, error } = useQuery<QualitySummaryResponse>({
    queryKey: ['gp-quality-summary', scoreFilter, issueFilter, typeFilter, activeTenantIds],
    queryFn: () =>
      apiClient.get(`/business-partners/quality-summary?${params.toString()}`).then((r) => r.data),
    enabled: activeTab === 'alle',
  })

  const bulkEnrich = useMutation({
    mutationFn: (ids: string[]) =>
      apiClient.post('/business-partners/bulk-enrich', { bpIds: ids }).then((r) => r.data),
    onSuccess: () => {
      toast({ title: 'KI-Anreicherung gestartet', tone: 'success' })
      setSelectedIds(new Set())
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler', description: err.message, tone: 'danger' })
    },
  })

  const bulkSave = useMutation({
    mutationFn: ({
      field,
      updates,
    }: {
      field: string
      updates: Array<{ id: string; value: string | null }>
    }) =>
      apiClient
        .patch('/business-partners/bulk-field-update', { field, updates })
        .then((r) => r.data as { success: string[]; failed: Array<{ id: string; error: string }> }),
    onSuccess: (result) => {
      const newSaved = new Set([...savedIdsRef.current, ...result.success])
      setSavedIds(newSaved)
      const newFailed = new Map(failedIdsRef.current)
      for (const f of result.failed) newFailed.set(f.id, f.error)
      setFailedIds(newFailed)

      if (result.failed.length === 0) {
        toast({ title: `${result.success.length} GP${result.success.length !== 1 ? 's' : ''} gespeichert`, tone: 'success' })
        void queryClient.invalidateQueries({ queryKey: ['gp-quality-summary'] })
      } else {
        toast({
          title: `${result.success.length} gespeichert, ${result.failed.length} fehlgeschlagen`,
          tone: 'warning',
        })
      }
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler beim Speichern', description: err.message, tone: 'danger' })
    },
  })

  const rows = data?.data ?? []
  const total = data?.total ?? 0

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id))

  function toggleAll() {
    if (allSelected) setSelectedIds(new Set())
    else setSelectedIds(new Set(rows.map((r) => r.id)))
  }

  function toggleRow(id: string, checked: boolean) {
    const next = new Set(selectedIds)
    if (checked) next.add(id)
    else next.delete(id)
    setSelectedIds(next)
  }

  function setFilter<T extends string>(setter: (v: T) => void, current: T, next: T) {
    const newVal = current === next ? ('' as T) : next
    setter(newVal)
    setInlineEdits(new Map())
    setSavedIds(new Set())
    setFailedIds(new Map())
  }

  function handleInlineChange(id: string, value: string) {
    setInlineEdits((prev) => {
      const next = new Map(prev)
      next.set(id, value)
      return next
    })
    setSavedIds((prev) => { const n = new Set(prev); n.delete(id); return n })
    setFailedIds((prev) => { const n = new Map(prev); n.delete(id); return n })
    setSelectedIds((prev) => { const n = new Set(prev); n.add(id); return n })
  }

  function handleBulkSave() {
    if (!editableField) return
    const updates: Array<{ id: string; value: string | null }> = []
    for (const id of selectedIds) {
      const editedValue = inlineEdits.get(id)
      if (editedValue !== undefined) {
        updates.push({ id, value: editedValue.trim() || null })
      }
    }
    if (updates.length === 0) {
      toast({ title: 'Keine Änderungen', description: 'Bitte zuerst Werte eintragen', tone: 'info' })
      return
    }
    bulkSave.mutate({ field: editableField, updates })
  }

  const selectionCount = selectedIds.size
  const dirtyCount = [...selectedIds].filter((id) => inlineEdits.has(id)).length

  const columns: ColumnDef<DatenpflegeRow, unknown>[] = [
    {
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          aria-label="Alle auswählen"
          className="w-4 h-4 rounded border-gray-300 accent-brand-700 cursor-pointer"
          checked={allSelected}
          onChange={toggleAll}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          aria-label={`${row.original.name} auswählen`}
          className="w-4 h-4 rounded border-gray-300 accent-brand-700 cursor-pointer"
          checked={selectedIds.has(row.original.id)}
          onChange={(e) => {
            e.stopPropagation()
            toggleRow(row.original.id, e.target.checked)
          }}
        />
      ),
      size: 44,
      enableSorting: false,
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      size: 260,
    },
    {
      id: 'tenantCode',
      header: () => <span className="block text-center">Mandant</span>,
      accessorKey: 'tenantCode',
      size: 110,
      cell: ({ row }) =>
        row.original.tenantCode ? (
          <div className="flex justify-center">
            <StatusBadge status={row.original.tenantCode} />
          </div>
        ) : (
          <span className="block text-center text-gray-400 text-xs">—</span>
        ),
    },
    {
      id: 'sapCardCode',
      header: 'KdNr',
      accessorKey: 'sapCardCode',
      size: 110,
      cell: ({ row }) =>
        row.original.sapCardCode ? (
          <code className="text-xs">{row.original.sapCardCode}</code>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      id: 'industryName',
      header: 'Branche',
      accessorKey: 'industryName',
      size: 200,
      cell: ({ row }) => {
        if (editableField === 'industryName') {
          return (
            <InlineEditCell
              rowId={row.original.id}
              currentValue={row.original.industryName}
              edits={inlineEditsRef.current}
              savedIds={savedIdsRef.current}
              failedIds={failedIdsRef.current}
              onChange={handleInlineChange}
            />
          )
        }
        return row.original.industryName ? (
          <span className="text-body text-gray-800">{row.original.industryName}</span>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        )
      },
    },
    {
      id: 'bpType',
      header: 'Typ',
      accessorKey: 'bpType',
      size: 110,
      cell: ({ getValue }) => {
        const v = getValue() as string
        return (
          <StatusBadge status={TYPE_STATUS[v] ?? 'default'} label={TYPE_LABEL[v] ?? v} />
        )
      },
    },
    {
      id: 'qualityScore',
      header: 'Qualitäts-Score',
      accessorKey: 'qualityScore',
      size: 160,
      cell: ({ getValue }) => <QualityScoreBar score={getValue() as number} />,
    },
    {
      id: 'qualityIssues',
      header: 'Fehlende Felder',
      accessorKey: 'qualityIssues',
      size: 260,
      enableSorting: false,
      cell: ({ getValue }) => {
        const issues = getValue() as QualityIssue[]
        const errors = issues.filter((i) => i.severity !== 'info')
        if (!errors.length)
          return <Badge variant="success">Vollständig</Badge>
        return (
          <span className="flex flex-wrap gap-1">
            {errors.map((issue) => (
              <Badge key={issue.code} variant={SEVERITY_VARIANT[issue.severity] ?? 'neutral'}>
                {ISSUE_LABEL[issue.code] ?? issue.code}
              </Badge>
            ))}
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      size: 48,
      enableSorting: false,
      cell: ({ row }) => (
        <IconButton
          icon={<ChevronRight className="w-4 h-4" />}
          aria-label={`Wizard für ${row.original.name} starten`}
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/gp/datenpflege/wizard?id=${row.original.id}`)
          }}
        />
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Datenpflege"
        description="Qualitäts-Scores und Datenlücken aller Geschäftspartner"
        breadcrumbs={[
          { label: 'Geschäftspartner', to: '/gp' },
          { label: 'Datenpflege' },
        ]}
        actions={
          <Button variant="primary" onClick={() => navigate('/gp/datenpflege/wizard')}>
            <PlayCircle className="w-4 h-4 mr-2" />
            Geführten Prozess starten
          </Button>
        }
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'alle' | 'kategorien')}>
        <TabsList>
          <TabsTrigger value="kategorien">Nach Kategorie</TabsTrigger>
          <TabsTrigger value="alle">
            Alle GPs{total > 0 && activeTab === 'alle' ? ` (${total})` : ''}
          </TabsTrigger>
        </TabsList>

        {/* ---- Kategorien-Ansicht ---- */}
        <TabsContent value="kategorien" className="mt-4">
          <KategorienAnsicht bpType={typeFilter} />
        </TabsContent>

        {/* ---- Alle-GPs-Tabellen-Ansicht ---- */}
        <TabsContent value="alle" className="mt-4 space-y-4">
          {/* Inline-Edit-Hinweis */}
          {editableField && !isLoading && rows.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <Save className="w-4 h-4 shrink-0 text-blue-500" />
              <span>
                Direkt-Bearbeitung aktiv — Feld <strong>{ISSUE_LABEL[issueFilter] ?? issueFilter}</strong> ist in jeder Zeile editierbar.
                Kunden ohne Änderung einfach abwählen, dann auf „Ausgewählte speichern" klicken.
              </span>
            </div>
          )}

          {/* Bulk-Aktionsleiste */}
          {selectionCount > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-brand-50 border border-brand-200 rounded-lg">
              <span className="text-body font-medium text-brand-700">
                {selectionCount} {selectionCount === 1 ? 'GP' : 'GPs'} ausgewählt
              </span>
              <div className="flex items-center gap-2">
                {editableField && dirtyCount > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    loading={bulkSave.isPending}
                    onClick={handleBulkSave}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {dirtyCount} {dirtyCount === 1 ? 'Änderung' : 'Änderungen'} speichern
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  loading={bulkEnrich.isPending}
                  onClick={() => bulkEnrich.mutate([...selectedIds])}
                >
                  <Bot className="w-4 h-4 mr-2" />
                  KI-Anreicherung starten
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/gp/datenpflege/wizard')}
                >
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Wizard starten
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => setSelectedIds(new Set())}
              >
                <X className="w-4 h-4 mr-1" />
                Auswahl aufheben
              </Button>
            </div>
          )}

          {/* Filter-Leiste */}
          <FilterBar>
            <FilterChip
              active={scoreFilter === 'lt100'}
              onClick={() => setFilter(setScoreFilter, scoreFilter, 'lt100')}
              onRemove={scoreFilter === 'lt100' ? () => setScoreFilter('') : undefined}
            >
              Score &lt; 100
            </FilterChip>
            <FilterChip
              active={scoreFilter === 'lt50'}
              onClick={() => setFilter(setScoreFilter, scoreFilter, 'lt50')}
              onRemove={scoreFilter === 'lt50' ? () => setScoreFilter('') : undefined}
            >
              Score &lt; 50
            </FilterChip>

            <span className="w-px h-5 bg-gray-200 self-center mx-1" aria-hidden />

            <FilterChip
              active={issueFilter === 'no_contact_email'}
              onClick={() => setFilter(setIssueFilter, issueFilter, 'no_contact_email')}
              onRemove={issueFilter === 'no_contact_email' ? () => setIssueFilter('') : undefined}
            >
              E-Mail fehlt
            </FilterChip>
            <FilterChip
              active={issueFilter === 'no_banking'}
              onClick={() => setFilter(setIssueFilter, issueFilter, 'no_banking')}
              onRemove={issueFilter === 'no_banking' ? () => setIssueFilter('') : undefined}
            >
              Bankdaten fehlen
            </FilterChip>
            <FilterChip
              active={issueFilter === 'missing_vat'}
              onClick={() => setFilter(setIssueFilter, issueFilter, 'missing_vat')}
              onRemove={issueFilter === 'missing_vat' ? () => setIssueFilter('') : undefined}
            >
              USt-ID fehlt
            </FilterChip>
            <FilterChip
              active={issueFilter === 'addr_street'}
              onClick={() => setFilter(setIssueFilter, issueFilter, 'addr_street')}
              onRemove={issueFilter === 'addr_street' ? () => setIssueFilter('') : undefined}
            >
              Adresse fehlt
            </FilterChip>
            <FilterChip
              active={issueFilter === 'missing_sap_id'}
              onClick={() => setFilter(setIssueFilter, issueFilter, 'missing_sap_id')}
              onRemove={issueFilter === 'missing_sap_id' ? () => setIssueFilter('') : undefined}
            >
              KdNr fehlt
            </FilterChip>
            <FilterChip
              active={issueFilter === 'missing_tenant'}
              onClick={() => setFilter(setIssueFilter, issueFilter, 'missing_tenant')}
              onRemove={issueFilter === 'missing_tenant' ? () => setIssueFilter('') : undefined}
            >
              Mandant fehlt
            </FilterChip>
            <FilterChip
              active={issueFilter === 'missing_industry'}
              onClick={() => setFilter(setIssueFilter, issueFilter, 'missing_industry')}
              onRemove={issueFilter === 'missing_industry' ? () => setIssueFilter('') : undefined}
            >
              Branche fehlt
            </FilterChip>
            <FilterChip
              active={issueFilter === 'missing_website'}
              onClick={() => setFilter(setIssueFilter, issueFilter, 'missing_website')}
              onRemove={issueFilter === 'missing_website' ? () => setIssueFilter('') : undefined}
            >
              Website fehlt
            </FilterChip>

            <span className="w-px h-5 bg-gray-200 self-center mx-1" aria-hidden />

            {(['customer', 'supplier', 'prospect'] as const).map((t) => (
              <FilterChip
                key={t}
                active={typeFilter === t}
                onClick={() => setFilter(setTypeFilter, typeFilter, t)}
                onRemove={typeFilter === t ? () => setTypeFilter('') : undefined}
              >
                {TYPE_LABEL[t]}
              </FilterChip>
            ))}
          </FilterBar>

          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState
              title="Fehler beim Laden"
              description={(error as Error)?.message ?? 'Unbekannter Fehler'}
            />
          ) : !rows.length ? (
            <EmptyState
              icon={<Building2 className="w-8 h-8" />}
              title="Keine Geschäftspartner gefunden"
              description="Für die aktiven Filter wurden keine Einträge gefunden."
            />
          ) : (
            <DataTable
              data={rows}
              columns={columns}
              onRowClick={(row) => {
                if (editableField) return
                navigate(`/gp/${row.id}`)
              }}
              pagination={{ pageSize: 50 }}
              searchable={false}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
